import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { useRouter } from "next/router";
import React from "react";

import BeegruPremium from "../../../../public/images/illustrations/Intro/agent/beegru_premium.svg";
import Boost from "../../../../public/images/illustrations/Intro/agent/boost.svg";
import Dashboard from "../../../../public/images/illustrations/Intro/agent/dashboard.svg";
import People from "../../../../public/images/illustrations/Intro/agent/people.svg";
import ListCards from "../../../cards/introListCard";

/* Styled Components */

const HeroSection = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	background: "#FEF2E9",
	minHeight: "300px",
	padding: "3rem 1.5rem",
	textAlign: "center",
	[theme.breakpoints.down("sm")]: {
		padding: "2rem 1.5rem",
		minHeight: "150px",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "60px",
	fontWeight: "300",
	lineHeight: "72px",
	color: "#000000",
	margin: "0rem 0rem 1rem 0rem",
	[theme.breakpoints.down("md")]: {
		fontSize: "48px",
		lineHeight: "58px",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "24px",
		lineHeight: "29px",
	},
}));

const SubText = styled(Typography)(({ theme }) => ({
	fontSize: "1.5rem",
	fontWeight: "400",
	color: "#666666",
	maxWidth: "none",
	margin: "0 auto",
	whiteSpace: "nowrap",
	textAlign: "center",
	[theme.breakpoints.down("md")]: {
		fontSize: "20px",
		whiteSpace: "normal",
		maxWidth: "800px",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
		whiteSpace: "normal",
		maxWidth: "100%",
	},
}));

const CardsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	gap: "1rem",
	padding: "2rem 12rem 2rem 12rem",
	[theme.breakpoints.down("md")]: {
		padding: "2rem 1rem",
	},
}));

const CardRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "2rem",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
	[theme.breakpoints.up("lg")]: {
		"& :hover": {
			cursor: "pointer",
			transform: "scale(1.02)",
			transition: "transform 0.2s ease-in-out",
		},
	},
}));

const UnlockContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	padding: "2rem 12rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		alignItems: "center",
		padding: "1rem",
		gap: "1rem",
		margin: "0rem 0rem 2rem 0rem",
	},
}));

const UnlockCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	backgroundColor: "#FFF6EF",
	padding: "2rem",
	borderRadius: "1.5rem",
	width: "100%",
	margin: "1rem 0rem",
	gap: "2rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "row",
		gap: "1rem",
		padding: "1rem 1rem",
		margin: "0rem",
	},
	[theme.breakpoints.down("sm")]: {
		alignItems: "flex-end",
		padding: "1rem 1rem 0rem 1rem",
	},
}));

const UnlockTextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	flex: 1,
}));

const UnlockHeading = styled(Typography)(({ theme }) => ({
	fontSize: "2.125rem",
	fontWeight: "400",
	color: "#621B16",
	marginBottom: "1rem",
	lineHeight: "2.5rem",
	[theme.breakpoints.down("md")]: {
		fontSize: "1rem",
		whiteSpace: "nowrap",
		marginBottom: "0rem",
	},
}));

const UnlockSubHeading = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "400",
	color: "#212121",
	lineHeight: "1.75rem",
	width: "100%",
	textAlign: "left",
	marginBottom: "1.5rem",
	[theme.breakpoints.down("md")]: {
		fontSize: "0.875rem",
		lineHeight: "1.5rem",
		marginBottom: "0.5rem",
	},
}));

const UnlockButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#6E3037",
	padding: "0.75rem 1.5rem",
	fontSize: "0.875rem",
	alignSelf: "flex-start",
	boxShadow: "none",
	borderRadius: "0.5rem",
	textTransform: "none",
	color: "#FFFFFF",
	"&:hover": {
		backgroundColor: "#5A262B",
	},
	[theme.breakpoints.down("md")]: {
		fontSize: "0.75rem",
		padding: "0.625rem 1.25rem",
		marginBottom: "1.5rem",
	},
}));

const UnlockImageContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	[theme.breakpoints.down("md")]: {
		width: "100%",
		height: "auto",
	},
	[theme.breakpoints.down("sm")]: {
		alignSelf: "flex-end",
		justifyContent: "flex-end",
	},
	"& img": {
		width: "359px",
		height: "236px",
		[theme.breakpoints.down("md")]: {
			width: "100%",
			height: "auto",
			maxWidth: "300px",
		},
		[theme.breakpoints.down("sm")]: {
			width: "99px",
			height: "80px",
			display: "block",
		},
	},
}));

const AdditionalBenefitsSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: "2rem",
	width: "100%",
	padding: "4rem 12rem 4rem 12rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		padding: "0rem 1rem 2rem 1rem",
	},
}));

const MobileOnlySection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	padding: "0rem 1rem 2rem 1rem",
}));

const MobileBenefitsDescription = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
	marginBottom: "2rem",
}));

const MobileFAQSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
	paddingTop: "2rem",
}));

const BenefitsColumn = styled(Box)(({ theme }) => ({
	flex: 1,
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
	fontSize: "1.125rem",
	fontWeight: "500",
	color: "#FC8019",
	letterSpacing: "0.05em",
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
	fontSize: "34px",
	fontWeight: "400",
	color: "#000000",
	lineHeight: "1.2",
	[theme.breakpoints.down("md")]: {
		fontSize: "24px",
	},
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
	fontSize: "16px",
	fontWeight: "400",
	color: "#000000",
	lineHeight: "1.75",
	textAlign: "justify",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
	},
}));

const FAQContainer = styled(Box)(({ theme }) => ({
	backgroundColor: "#FAFAFA",
	borderRadius: "1rem",
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	padding: "0rem 0rem 0rem 0rem",
}));

const AccordionWrapper = styled(Box)(({ theme }) => ({
	borderRadius: "0.5rem",
	border: "0.0625rem solid #C4C4C4",
	overflow: "hidden",
}));

const AccordionItem = styled(Box)(({ theme }) => ({
	borderBottom: "0.0625rem solid #C4C4C4",
	"&:last-child": {
		borderBottom: "none",
	},
}));

const AccordionHeader = styled(Box)<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 2rem",
	cursor: "pointer",
	backgroundColor: isOpen ? "#F5F5F5" : "#FFFFFF",
	"&:hover": {
		backgroundColor: "#FAFAFA",
	},
	[theme.breakpoints.down("md")]: {
		padding: "0.75rem 1rem",
	},
}));

const AccordionQuestion = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "500",
	color: "#212121",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
	},
}));

const AccordionIcon = styled(Box)(({ theme }) => ({
	width: "1.5rem",
	height: "1.5rem",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontSize: "1.5rem",
	color: "#212121",
	fontWeight: "300",
	transition: "transform 0.1875s ease",
}));

const AccordionContent = styled(Box)<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
	padding: "0 2rem 1rem 2rem",
	fontSize: "1rem",
	lineHeight: "1.75",
	color: "#616161",
	backgroundColor: isOpen ? "#F5F5F5" : "#FFFFFF",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
		padding: "0 1rem 0.75rem 1rem",
	},
}));

const PropertySeekerNewIntro = () => {
	const router = useRouter();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [openAccordion, setOpenAccordion] = React.useState<number | null>(0);

	const toggleAccordion = (index: number) => {
		setOpenAccordion(openAccordion === index ? null : index);
	};

	const faqs = [
		{
			question: "Do I need an account to browse property listings?",
			answer: "No. You can explore unlimited properties without creating an account.",
		},
		{
			question: "Is Beegru a property finder near me or only in Bangalore?",
			answer: "Beegru works across locations, with strong coverage as a property finder Bangalore users trust.",
		},
		{
			question: "How do I contact the lister of a property?",
			answer: "Use the call or WhatsApp buttons on the property listing—direct, no forms.",
		},
		{
			question: "What happens to my phone number or data?",
			answer: "We don't sell seeker data. You choose when to share your number—no spam.",
		},
		{
			question: "Can I filter by types of property?",
			answer: "Yes. Filter by property type (e.g., 2 BHK, villa), locality, price, and amenities.",
		},
		{
			question: "How do I know a listing is trustworthy?",
			answer: "Look for verified lister badges and complete details; Boosted posts are clearly marked.",
		},
	];

	const localLang = {
		hassleFreeSearch:
			router.locale === "ar-AE"
				? "بحث خالٍ من المتاعب"
				: router.locale === "kn-IN"
					? "ತೊಂದರೆ-ಮುಕ್ತ ಹುಡುಕಾಟ"
					: "Hassle-free search",

		directContact:
			router.locale === "ar-AE" ? "التواصل المباشر" : router.locale === "kn-IN" ? "ನೇರ ಸಂಪರ್ಕ" : "Direct contact",

		privacyTrust:
			router.locale === "ar-AE"
				? "الخصوصية والثقة"
				: router.locale === "kn-IN"
					? "ಗೌಪ್ಯತೆ ಮತ್ತು ನಂಬಿಕೆ"
					: "Privacy & trust",

		shortlistSharing:
			router.locale === "ar-AE"
				? "القائمة المختصرة والمشاركة"
				: router.locale === "kn-IN"
					? "ಶಾರ್ಟ್‌ಲಿಸ್ಟ್ ಮತ್ತು ಹಂಚಿಕೆ"
					: "Shortlist & sharing",

		listHassleFreeSearch: [
			router.locale === "ar-AE"
				? "تصفح بحرية - بدون حساب، بدون جدران دفع"
				: router.locale === "kn-IN"
					? "ಮುಕ್ತವಾಗಿ ಬ್ರೌಸ್ ಮಾಡಿ—ಖಾತೆ ಇಲ್ಲ, ಪೇವಾಲ್‌ಗಳು ಇಲ್ಲ"
					: "Browse freely—no account, no paywalls",
			router.locale === "ar-AE"
				? "نتائج بسيطة وخالية من التشتيت"
				: router.locale === "kn-IN"
					? "ಕನಿಷ್ಠ, ವಿಚಲನ-ಮುಕ್ತ ಫಲಿತಾಂಶಗಳು"
					: "Minimal, distraction-free results",
			router.locale === "ar-AE"
				? "مرشحات موثوقة: السعر والموقع ونوع العقار"
				: router.locale === "kn-IN"
					? "ವಿಶ್ವಾಸಾರ್ಹ ಫಿಲ್ಟರ್‌ಗಳು: ಬೆಲೆ, ಸ್ಥಳ, ಆಸ್ತಿ ಪ್ರಕಾರ"
					: "Reliable filters: price, locality, property type",
		],

		listDirectContact: [
			router.locale === "ar-AE"
				? "اتصل أو استخدم WhatsApp من صفحة القائمة"
				: router.locale === "kn-IN"
					? "ಪಟ್ಟಿ ಪುಟದಿಂದ ಕರೆ ಮಾಡಿ ಅಥವಾ WhatsApp ಮಾಡಿ"
					: "Call or WhatsApp from the listing page",
			router.locale === "ar-AE"
				? "راسل البنائين أو المالكين أو الوكلاء مباشرة"
				: router.locale === "kn-IN"
					? "ಬಿಲ್ಡರ್‌ಗಳು, ಮಾಲೀಕರು ಅಥವಾ ಏಜೆಂಟ್‌ಗಳಿಗೆ ನೇರವಾಗಿ ಸಂದೇಶ ಕಳುಹಿಸಿ"
					: "Message builders, owners, or agents directly",
			router.locale === "ar-AE"
				? "بدون نماذج أو إعادة توجيه أو تأخير"
				: router.locale === "kn-IN"
					? "ಫಾರ್ಮ್‌ಗಳು, ಮರುಮಾರ್ಗಗಳು ಅಥವಾ ವಿಳಂಬಗಳಿಲ್ಲ"
					: "No forms, reroutes, or delays",
		],

		listPrivacyTrust: [
			router.locale === "ar-AE"
				? "نحن لا نبيع بياناتك - بدون رسائل غير مرغوب فيها"
				: router.locale === "kn-IN"
					? "ನಾವು ನಿಮ್ಮ ಡೇಟಾವನ್ನು ಮಾರಾಟ ಮಾಡುವುದಿಲ್ಲ—ಸ್ಪ್ಯಾಮ್ ಇಲ್ಲ"
					: "We don't sell your data—no spam",
			router.locale === "ar-AE"
				? "رقمك يبقى لك حتى تشاركه"
				: router.locale === "kn-IN"
					? "ನೀವು ಹಂಚಿಕೊಳ್ಳುವವರೆಗೆ ನಿಮ್ಮ ಸಂಖ್ಯೆ ನಿಮ್ಮದಾಗಿರುತ್ತದೆ"
					: "Your number stays yours until you share",
			router.locale === "ar-AE"
				? "شاهد شارات التحقق والمنشورات المعززة"
				: router.locale === "kn-IN"
					? "ಪರಿಶೀಲಿಸಿದ ಬ್ಯಾಡ್ಜ್‌ಗಳು ಮತ್ತು ಬೂಸ್ಟ್ ಪೋಸ್ಟ್‌ಗಳನ್ನು ನೋಡಿ"
					: "See verified badges and Boosted posts",
		],

		listShortlistSharing: [
			router.locale === "ar-AE"
				? "احفظ المفضلة للمقارنة لاحقاً"
				: router.locale === "kn-IN"
					? "ನಂತರ ಹೋಲಿಸಲು ಮೆಚ್ಚಿನವುಗಳನ್ನು ಉಳಿಸಿ"
					: "Save favorites to compare later",
			router.locale === "ar-AE"
				? "شارك العقارات عبر الرابط أو WhatsApp"
				: router.locale === "kn-IN"
					? "ಲಿಂಕ್ ಅಥವಾ WhatsApp ಮೂಲಕ ಆಸ್ತಿಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳಿ"
					: "Share properties via link or WhatsApp",
			router.locale === "ar-AE"
				? "قم بزيارة المنازل المحفوظة في أي وقت"
				: router.locale === "kn-IN"
					? "ಯಾವಾಗ ಬೇಕಾದರೂ ಉಳಿಸಿದ ಮನೆಗಳನ್ನು ಮರುಭೇಟಿ ಮಾಡಿ"
					: "Revisit saved homes anytime",
		],
	};

	return (
		<>
			<HeroSection>
				<Heading>
					Benefits for <span style={{ color: "#FC8019" }}>Property Seekers</span>
				</Heading>
				<SubText variant="h5">
					A clean property finder experience: browse property listings with no login, contact listers directly, and keep
					your data private.
				</SubText>
			</HeroSection>
			<CardsContainer>
				<CardRow>
					<ListCards
						heading={localLang.hassleFreeSearch}
						list_text={localLang.listHassleFreeSearch}
						icon={People}
					/>
					<ListCards
						heading={localLang.directContact}
						list_text={localLang.listDirectContact}
						icon={Dashboard}
					/>
				</CardRow>
				<CardRow>
					<ListCards
						heading={localLang.privacyTrust}
						list_text={localLang.listPrivacyTrust}
						icon={Boost}
					/>
					<ListCards
						heading={localLang.shortlistSharing}
						list_text={localLang.listShortlistSharing}
						icon={BeegruPremium}
					/>
				</CardRow>
			</CardsContainer>

			<UnlockContainer>
				<UnlockCard>
					<UnlockTextContainer>
						<UnlockHeading>Find your next space, stress‑free</UnlockHeading>
						<UnlockSubHeading variant="body1">
							Browse verified listings, connect instantly, and move forward with confidence
						</UnlockSubHeading>
						<UnlockButton
							variant="contained"
							size="small"
							href="/search"
						>
							Begin your search
						</UnlockButton>
					</UnlockTextContainer>
					<UnlockImageContainer>
						<img
							src="/images/aboutus/property-seeker.svg"
							alt="Property Seeker Illustration"
						/>
					</UnlockImageContainer>
				</UnlockCard>
			</UnlockContainer>

			{isMobile ? (
				<MobileOnlySection>
					<MobileBenefitsDescription>
						<SectionLabel>Additional Benefits</SectionLabel>
						<SectionHeading>Find Property the Clean, Direct Way</SectionHeading>
						<DescriptionText>
							Beegru is a minimalist property finder for property seekers: browse properties with no login, use reliable
							filters by budget, locality, and property type, and contact listers directly - no forms, no spam.
							It&apos;s the property finder Bangalore users trust for private, clutter-free search.
						</DescriptionText>
						<DescriptionText>
							Save favourites to revisit later and compare easily. Learn how Beegru keeps search fast and private while
							connecting you directly to verified listers.
						</DescriptionText>
					</MobileBenefitsDescription>

					<MobileFAQSection>
						<SectionLabel>Frequently Asked Questions</SectionLabel>
						<FAQContainer>
							<AccordionWrapper>
								{faqs.map((faq, index) => (
									<AccordionItem key={index}>
										<AccordionHeader
											isOpen={openAccordion === index}
											onClick={() => toggleAccordion(index)}
										>
											<AccordionQuestion>{faq.question}</AccordionQuestion>
											<AccordionIcon>{openAccordion === index ? "−" : "+"}</AccordionIcon>
										</AccordionHeader>
										{openAccordion === index && (
											<AccordionContent isOpen={openAccordion === index}>{faq.answer}</AccordionContent>
										)}
									</AccordionItem>
								))}
							</AccordionWrapper>
						</FAQContainer>
					</MobileFAQSection>
				</MobileOnlySection>
			) : (
				<AdditionalBenefitsSection>
					<BenefitsColumn>
						<SectionLabel>Additional Benefits</SectionLabel>
						<SectionHeading>Find Property the Clean, Direct Way</SectionHeading>
						<DescriptionText>
							Beegru is a minimalist property finder for property seekers: browse properties with no login, use reliable
							filters by budget, locality, and property type, and contact listers directly - no forms, no spam. It’s the
							property finder Bangalore users trust for private, clutter-free search.
						</DescriptionText>
						<DescriptionText>
							Save favourites to revisit later and compare easily. Learn how Beegru keeps search fast and private while
							connecting you directly to verified listers.
						</DescriptionText>
					</BenefitsColumn>
					<BenefitsColumn>
						<SectionLabel>Frequently Asked Questions</SectionLabel>
						<FAQContainer>
							<AccordionWrapper>
								{faqs.map((faq, index) => (
									<AccordionItem key={index}>
										<AccordionHeader
											isOpen={openAccordion === index}
											onClick={() => toggleAccordion(index)}
										>
											<AccordionQuestion>{faq.question}</AccordionQuestion>
											<AccordionIcon>{openAccordion === index ? "−" : "+"}</AccordionIcon>
										</AccordionHeader>
										{openAccordion === index && (
											<AccordionContent isOpen={openAccordion === index}>{faq.answer}</AccordionContent>
										)}
									</AccordionItem>
								))}
							</AccordionWrapper>
						</FAQContainer>
					</BenefitsColumn>
				</AdditionalBenefitsSection>
			)}
		</>
	);
};

export default PropertySeekerNewIntro;
